import { ErrorResponse, UserManager, type User as OidcUser } from 'oidc-client-ts';
import type { Authentication } from '@/generic/authentication/domain/Authentication.ts';
import type { AuthenticationContextOpenId } from '@/generic/authentication/secondary/AuthenticationContextOpenId.ts';
import { Optional } from '@/generic/functional';
import { Token } from '@/generic/authentication/domain/Token.ts';
import { RequestedPage, SIGNIN_CALLBACK } from '@/generic/authentication/domain/RequestedPage.ts';

const SCOPE = 'openid profile';

const currentPage = (): string =>
  `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;

export class AuthenticationOpenId implements Authentication {
  readonly #userManager: UserManager;
  #renewal: Promise<Optional<OidcUser>> | undefined;

  constructor(context: AuthenticationContextOpenId) {
    this.#userManager = new UserManager({
      client_id: context.clientId,
      authority: context.authority,
      redirect_uri: `${globalThis.location.origin}${SIGNIN_CALLBACK}`,
      scope: SCOPE,
    });
    this.#userManager.events.addSilentRenewError((error) => {
      if (error instanceof ErrorResponse) {
        void this.loginAgain();
      }
    });
  }

  async login(): Promise<void> {
    if (globalThis.location.pathname === SIGNIN_CALLBACK) {
      return;
    }
    const user = await this.#userManager.getUser();
    if (user === null) {
      await this.#signinRedirect();
    }
  }

  async loginAgain(): Promise<void> {
    await this.#userManager.removeUser();
    await this.#signinRedirect();
  }

  async logout(): Promise<void> {
    await this.#userManager.signoutRedirect({
      post_logout_redirect_uri: globalThis.location.href,
    });
  }

  async loggedIn(): Promise<RequestedPage> {
    const user = await this.#userManager.signinCallback();

    return RequestedPage.fromPath(user?.state);
  }

  async getToken(): Promise<Optional<Token>> {
    const user = await this.#validUser();

    return user.map((user) => new Token(user.access_token));
  }

  async #validUser(): Promise<Optional<OidcUser>> {
    const user = Optional.ofFalsifiable(await this.#userManager.getUser());
    const expired = user.map((user) => user.expired === true).orElse(false);

    if (!expired) {
      return user;
    }

    this.#renewal ??= this.#renewUser().finally(() => {
      this.#renewal = undefined;
    });

    return this.#renewal;
  }

  async #renewUser(): Promise<Optional<OidcUser>> {
    const user = Optional.ofFalsifiable(await this.#userManager.signinSilent().catch(() => null));

    if (user.isEmpty()) {
      await this.loginAgain();
    }

    return user;
  }

  async #signinRedirect(): Promise<void> {
    await this.#userManager.signinRedirect({ state: currentPage() });
  }
}
